import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { DocumentCheckIcon } from '@heroicons/react/24/outline';
import { PDFDownloadLink } from '@react-pdf/renderer';

import { useAppSelector } from 'src/app/store/hooks';
import {
  useOrganizationSettingsLogoRetrieveQuery,
  useVehiclesInspectionsRetrieveQuery,
  VehicleInspectionReportDetailsRead,
} from 'src/common/external/bambi-api/bambiApi';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import { Modal, ModalProps } from 'src/common/primitives/Modal';
import { selectCurrentOrganization } from 'src/features/auth/selectors';

import { VehicleInspectionReportDocument } from './reports/VehicleInspectionReportDocument';

interface VehicleInspectionsDownloadPDFReportModalProps {
  inspection: VehicleInspectionReportDetailsRead;
  open: ModalProps['open'];
  setOpen: ModalProps['setOpen'];
}
export function VehicleInspectionsDownloadPDFReportModal({
  inspection,
  open,
  setOpen,
}: VehicleInspectionsDownloadPDFReportModalProps) {
  const organization = useAppSelector(selectCurrentOrganization);
  const { data, isLoading: isLoadingLogo } =
    useOrganizationSettingsLogoRetrieveQuery({});

  const { data: inspectionData, isLoading: isLoadingInspectionData } =
    useVehiclesInspectionsRetrieveQuery(
      {
        id: inspection.id,
      },
      {
        skip: !open,
      }
    );

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      testId="vehicle-inspection-report-download-modal"
      title="Download Inspection Report"
    >
      {isLoadingLogo || isLoadingInspectionData ? (
        <div className="flex flex-row justify-center">
          <LoadingIndicator />
        </div>
      ) : !inspectionData ? (
        <div>
          <FormErrorMessage>
            Something went wrong, unable to retrieve inspection data
          </FormErrorMessage>
        </div>
      ) : (
        <PDFDownloadLink
          document={
            <VehicleInspectionReportDocument
              organizationName={organization?.name ?? ''}
              logo={data?.logo}
              inspection={inspectionData}
            />
          }
          fileName={`vehicle-inspection-report-${inspectionData.created_at}.pdf`}
        >
          {(instance) => {
            if (instance.loading || !instance.url) {
              return (
                <div className="mt-2 flex flex-row justify-center">
                  <div className="flex flex-col items-center gap-2">
                    <img
                      className="aspect-square h-12 w-12"
                      src={process.env.PUBLIC_URL + '/icons/document.gif'}
                      alt="Rendering PDF"
                      data-attribution="Paper animated icons created by Freepik - Flaticon"
                    />
                    Rendering PDF
                  </div>
                </div>
              );
            }

            // Error rendering PDF
            if (instance.error) {
              return (
                <div className="mt-2 flex flex-row justify-center">
                  <FormErrorMessage>
                    Something unexpected happened while rendering:{' '}
                    {instance.error.message}
                  </FormErrorMessage>
                </div>
              );
            }

            // PDF ready
            return (
              <div className="mt-2 flex flex-row justify-center">
                <div className="mt-2 flex flex-row">
                  <span className="text-green-500">
                    <DocumentCheckIcon width={32} height={32} />
                  </span>
                  <span
                    className="text-md border-none px-3 py-2 text-blue-600 underline"
                    onClick={() => {
                      // Delaying slightly because we need the link to open before
                      // closing the modal so we don't kill the reference to the blob
                      // that was just created
                      setTimeout(() => setOpen(false), 500);
                    }}
                  >
                    Click here to open the report
                  </span>
                </div>
              </div>
            );
          }}
        </PDFDownloadLink>
      )}
    </Modal>
  );
}
